import React from 'react';
import { Field, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';

import Loader from 'shared/blocks/selfcare/shared/components/Loader';
import InputField from 'shared/components/presentational/Form/Fields/InputField';
import OperatorsSelect from '../../../unauthenticated-renewal/form/components/OperatorsSelect';
import { phoneValidator } from '../../../unauthenticated-renewal/form/helpers/phoneNumberValidator';
import ResponsiveHoc, {
  ResponsiveHocProps
} from 'shared/components/presentational/ResponsiveHOC/ResponsiveHoc';
import AlertBox from 'shared/components/presentational/AlertBox/AlertBox';
import { useMobileOperators } from 'shared/blocks/one-shop/unauthenticated-renewal/form/hooks/useMobileOperators';
import { FormattedProps } from '../types';
import { Button } from '../../../shared/components/Button/Button';
import {
  AlertBoxContainer,
  ButtonsWrapper,
  FieldTitle,
  FieldWrapper,
  Form,
  Tax,
  TaxLabel,
  Wrapper
} from '../styles/components';
import { CustomTitle } from 'shared/components/presentational/Title/sharedTitles';
import { SAVE_PHONE_DATA, UpdatingOptionsState } from '../../reducer';
import OfferDetailsRocContainer from 'shared/blocks/one-shop/shared/containers/OfferDetailsRocContainer';

type MobileMoneyFormProps = FormattedProps & ResponsiveHocProps;

function MobileMoneyForm({
  title,
  phoneNumberTitle,
  mobileMoneyTitle,
  submitButton,
  previousButtonLabel,
  redirectLinkToStart,
  mediaQueries
}: MobileMoneyFormProps) {
  const history = useHistory();
  const { t } = useTranslation();

  const updatingOptionsValues = useSelector(
    (state: { updatingOptions: UpdatingOptionsState }) =>
      state.updatingOptions || {}
  );
  const dispatch = useDispatch();

  const {
    isLoading,
    isError,
    data: formattedOperators = []
  } = useMobileOperators({
    operators: updatingOptionsValues.operators
  });

  if (mediaQueries.toPhone === false) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="info"
          content={t('blocks.updatingOptions.onlyMobile')}
        />
      </AlertBoxContainer>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  if (
    isError ||
    !formattedOperators ||
    Object.keys(updatingOptionsValues).length === 0
  ) {
    return (
      <AlertBoxContainer>
        <AlertBox
          status="error"
          content={<Trans i18nKey="blocks.updatingOptions.error.description" />}
          link={{
            label: t('blocks.updatingOptions.error.linkLabel'),
            href: redirectLinkToStart
          }}
        />
      </AlertBoxContainer>
    );
  }

  const validationSchema = yup.object().shape({
    phoneNumber: yup
      .string()
      .required(
        t('blocks.updatingOptions.mobileMoneyForm.validation.phoneNumber')
      ),
    selectedOperator: yup
      .string()
      .required(t('blocks.updatingOptions.mobileMoneyForm.validation.operator'))
  });

  const handleCancelButton = () => {
    history.goBack();
  };

  return (
    <Wrapper>
      {title && (
        <CustomTitle htmlTag={title.htmlTag}>{title.label}</CustomTitle>
      )}
      <Formik
        initialValues={{ phoneNumber: '', selectedOperator: '' }}
        onSubmit={values => {
          dispatch({
            type: SAVE_PHONE_DATA,
            payload: {
              formValues: {
                phoneNumber: values.phoneNumber,
                selectedOperator: values.selectedOperator
              }
            }
          });
          history.push(submitButton.link);
        }}
        validationSchema={validationSchema}
      >
        <Form>
          <FieldWrapper>
            {phoneNumberTitle && (
              <FieldTitle htmlTag={phoneNumberTitle.htmlTag}>
                {phoneNumberTitle.label}
              </FieldTitle>
            )}
            <div>
              <Field
                component={InputField}
                name="phoneNumber"
                type="text"
                isRequired={true}
                placeholder={t(
                  'blocks.updatingOptions.mobileMoneyForm.phoneNumber'
                )}
                validate={phoneValidator}
              />
            </div>
          </FieldWrapper>

          <FieldWrapper>
            {mobileMoneyTitle && (
              <FieldTitle htmlTag={mobileMoneyTitle.htmlTag}>
                {mobileMoneyTitle.label}
              </FieldTitle>
            )}
            <OperatorsSelect operators={formattedOperators} />

            {updatingOptionsValues.payment.tax?.label && (
              <Tax>
                <TaxLabel>
                  {updatingOptionsValues.payment.tax.label}&nbsp;
                </TaxLabel>
                <OfferDetailsRocContainer
                  price={updatingOptionsValues.payment.tax.amount}
                  currency={updatingOptionsValues.payment.currency}
                  variant="text"
                />
              </Tax>
            )}
          </FieldWrapper>

          <ButtonsWrapper>
            <Button
              label={submitButton.label}
              variant="primary"
              fullWidthMobile
              type="submit"
            />

            {previousButtonLabel && (
              <Button
                label={previousButtonLabel}
                variant="secondary"
                fullWidthMobile
                fullWidthDesktop
                onClick={handleCancelButton}
              />
            )}
          </ButtonsWrapper>
        </Form>
      </Formik>
    </Wrapper>
  );
}

export default ResponsiveHoc(MobileMoneyForm);
